import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Truck, User, Calendar, Tag } from "react-feather";
import toast from "react-hot-toast";
import {
  useGetVehicleQuery,
  useCreateVehicleMutation,
  useUpdateVehicleMutation,
} from "../vehiclesApi";
import { useGetBranchesQuery } from "../../../admin/adminApi";
import { useLazySearchEmployeesQuery } from "../../../users/employeesApi";
import { useClaims } from "../../../auth/useClaims";
import { useCanCreateVehicles, useCanEditVehicles } from "../useVehiclePermissions";
import VehicleTypeSelect, { VehicleTypeBadge } from "../components/VehicleTypeSelect";
import { vehicleTypeLabel, vehicleTypeRequiresEmployee } from "../vehicleTypes";
import { paths } from "../../../../routes/paths";
import { useDefaultFormBranch } from "../../../branchFilter/useDefaultFormBranch";

const emptyForm = {
  branchId: "",
  regNo: "",
  brand: "",
  model: "",
  makeYear: "",
  lastServiceMileage: "",
  nextServiceDueMileage: "",
  isActive: true,
};

// Toggle-switch styling, same pattern already used on EmployeeForm.jsx.
const ToggleSwitch = ({ id, checked, onChange, disabled, label }) => (
  <div className="form-check form-switch">
    <input
      type="checkbox"
      className="form-check-input"
      id={id}
      style={{ width: "2.5em", height: "1.4em" }}
      checked={checked}
      onChange={onChange}
      disabled={disabled}
    />
    <label className="form-check-label ms-2" htmlFor={id}>{label}</label>
  </div>
);

// Vehicle Master (handoff §20.1) - fleet roster, mirrors the Employee
// master's architecture: own Add/Edit route pages, vehicleId is globally
// unique (unlike custId/productCode) so no {branchId} composite key is
// needed here.
//
// Allocation (Batch191, 2026-09-22): every vehicle carries one TYPE - Alarms,
// Operations, Technical, Staff or Sales - which replaced "allocated to a
// designation". Staff and Sales vehicles must also name an employee; the other
// three may. The API enforces the same rule, so the form's checks are only
// there to give the message before the round trip.
const VehicleForm = () => {
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(vehicleId);
  const canCreate = useCanCreateVehicles();
  const canEdit = useCanEditVehicles();
  const { branches: callerBranchCodes, isAllBranches } = useClaims();
  const { data: branches = [] } = useGetBranchesQuery();
  const {
    data: detail,
    isLoading: isLoadingDetail,
    isError: isDetailError,
    error: detailError,
  } = useGetVehicleQuery(vehicleId, { skip: !isEditing });
  const [createVehicle, { isLoading: isCreating }] = useCreateVehicleMutation();
  const [updateVehicle, { isLoading: isUpdating }] = useUpdateVehicleMutation();
  const [triggerEmployeeSearch] = useLazySearchEmployeesQuery();

  const availableBranches = isAllBranches
    ? branches
    : branches.filter((b) => callerBranchCodes?.includes(b.branchCode));

  const [form, setForm] = useState(emptyForm);
  // A new record starts on the header branch (useDefaultFormBranch, 2026-10-02); the
  // branch list loads async, so it is filled in once known unless already chosen.
  const defaultBranchId = useDefaultFormBranch(availableBranches);
  useEffect(() => {
    if (!isEditing && defaultBranchId !== "") setForm((f) => (f.branchId ? f : { ...f, branchId: defaultBranchId }));
  }, [defaultBranchId]); // eslint-disable-line react-hooks/exhaustive-deps
  const [vehicleType, setVehicleType] = useState("");
  // Employee allocation is a code-lookup, not a name picker - per direct
  // instruction, keeping it to a plain "type the code, find the name" text
  // box (the /employees/search endpoint already supports an exact-code
  // lookup, same one EmployeePicker.jsx uses internally) rather than the
  // fuller AsyncSelect name-search picker used on the Users screen.
  const [empCodeInput, setEmpCodeInput] = useState("");
  const [resolvedEmployee, setResolvedEmployee] = useState(null);
  const [isSearchingEmpCode, setIsSearchingEmpCode] = useState(false);

  const employeeRequired = vehicleTypeRequiresEmployee(vehicleType);

  const handleFindEmployee = async () => {
    const code = empCodeInput.trim();
    if (!code) return;
    setIsSearchingEmpCode(true);
    try {
      const { data = [] } = await triggerEmployeeSearch(code);
      const match = data.find((emp) => String(emp.empCode) === code) || data[0];
      if (match) {
        setResolvedEmployee(match);
        setEmpCodeInput("");
      } else {
        toast.error(`No employee found for code ${code}`);
      }
    } finally {
      setIsSearchingEmpCode(false);
    }
  };

  useEffect(() => {
    if (isEditing && detail) {
      const v = detail;
      setForm({
        ...emptyForm,
        ...v,
        isActive: Boolean(v.isActive),
      });
      setVehicleType(v.vehicleType || "");
      setResolvedEmployee(
        v.allocatedToEmployeeRecordGuid
          ? {
              recordGuid: v.allocatedToEmployeeRecordGuid,
              empName: v.allocatedToEmployeeName || v.allocatedToEmployeeRecordGuid,
              empLastName: "",
              empCode: "",
            }
          : null
      );
      setEmpCodeInput("");
    } else if (!isEditing) {
      setForm({
        ...emptyForm,
        branchId: defaultBranchId,
      });
      setVehicleType("");
      setResolvedEmployee(null);
      setEmpCodeInput("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, detail]);

  const setField = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const readOnly = isEditing ? !canEdit : !canCreate;

  const handleSave = async () => {
    if (!form.branchId) {
      toast.error("Branch is required");
      return;
    }
    if (!form.regNo.trim()) {
      toast.error("Registration number is required");
      return;
    }
    if (!vehicleType) {
      toast.error("Select the vehicle type");
      return;
    }
    if (employeeRequired && !resolvedEmployee) {
      toast.error(`A ${vehicleTypeLabel(vehicleType)} vehicle must be allocated to an employee - find one by code`);
      return;
    }

    const body = {
      branchId: Number(form.branchId),
      regNo: form.regNo.trim(),
      brand: form.brand.trim() || null,
      model: form.model.trim() || null,
      makeYear: form.makeYear === "" ? null : Number(form.makeYear),
      vehicleType,
      allocatedToEmployeeRecordGuid: resolvedEmployee ? resolvedEmployee.recordGuid : null,
      lastServiceMileage: form.lastServiceMileage === "" ? null : Number(form.lastServiceMileage),
      nextServiceDueMileage: form.nextServiceDueMileage === "" ? null : Number(form.nextServiceDueMileage),
      // Every update must resend isActive explicitly - verified live that
      // PUT silently defaults it to false if the field is omitted, it is
      // never preserved from the existing record automatically.
      isActive: form.isActive,
    };

    try {
      if (isEditing) {
        await updateVehicle({ vehicleId, ...body }).unwrap();
        toast.success("Vehicle saved");
        navigate(paths.vehicleEdit(vehicleId), { replace: true });
      } else {
        const created = await createVehicle(body).unwrap();
        toast.success("Vehicle saved");
        navigate(paths.vehicleEdit(created.vehicleId), { replace: true });
      }
    } catch (error) {
      toast.error(error?.message || "Could not save vehicle");
    }
  };

  if (isEditing && isDetailError) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <Link to={paths.vehicles} className="d-inline-flex align-items-center mb-3">
            <ArrowLeft size={16} className="me-1" /> Back to Vehicles
          </Link>
          <div className="alert alert-danger">
            Could not load this vehicle ({detailError?.message || "server error"}).
          </div>
        </div>
      </div>
    );
  }

  if (isEditing && isLoadingDetail) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <Link to={paths.vehicles} className="d-inline-flex align-items-center mb-2">
                <ArrowLeft size={16} className="me-1" /> Back to Vehicles
              </Link>
              <h4>{isEditing ? "Edit Vehicle" : "Add New Vehicle"}</h4>
            </div>
          </div>
        </div>

        {isEditing && (
          <div className="card mb-4">
            <div className="card-body d-flex align-items-center gap-3">
              <span className="master-detail-avatar master-detail-avatar-lg">
                {(form.regNo || "?").trim().slice(0, 2).toUpperCase()}
              </span>
              <div className="flex-grow-1">
                <h5 className="mb-0 d-flex align-items-center gap-2">
                  {form.regNo || "-"}
                  <VehicleTypeBadge code={form.vehicleType} />
                </h5>
                <div className="d-flex flex-wrap gap-3 mt-2 text-muted small">
                  <span className="d-inline-flex align-items-center">
                    <Truck size={14} className="me-1" />
                    {[form.brand, form.model].filter(Boolean).join(" ") || "-"}
                  </span>
                  <span className="d-inline-flex align-items-center">
                    <User size={14} className="me-1" />
                    {form.allocatedToEmployeeName || "Pool vehicle, no named driver"}
                  </span>
                  <span className="d-inline-flex align-items-center">
                    <Calendar size={14} className="me-1" />
                    {form.makeYear || "-"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {readOnly && (
          <div className="alert alert-info">
            You have read-only access. {isEditing ? "Editing" : "Creating"} vehicles requires
            the Vehicles.{isEditing ? "Edit" : "Create"} permission.
          </div>
        )}

        <div className="card mb-4">
          <div className="card-body">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h5 className="mb-0">Vehicle Details</h5>
              <ToggleSwitch
                id="vehicle-active"
                checked={form.isActive}
                onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                disabled={readOnly}
                label={form.isActive ? "Active" : "Not In Use"}
              />
            </div>
            <div className="row">
              <div className="col-md-4 mb-3">
                <label className="form-label">Branch</label>
                {isEditing ? (
                  <input
                    type="text"
                    className="form-control"
                    value={form.branchCode || branches.find((b) => b.branchId === form.branchId)?.branchCode || ""}
                    readOnly
                    disabled
                  />
                ) : (
                  <select
                    className="form-control"
                    value={form.branchId}
                    onChange={setField("branchId")}
                    disabled={readOnly}
                  >
                    <option value="">--Select--</option>
                    {availableBranches.map((b) => (
                      <option key={b.branchId} value={b.branchId}>{b.branchCode}</option>
                    ))}
                  </select>
                )}
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Registration No</label>
                <input
                  type="text"
                  className="form-control"
                  value={form.regNo}
                  onChange={setField("regNo")}
                  disabled={readOnly}
                />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Brand</label>
                <input type="text" className="form-control" value={form.brand || ""} onChange={setField("brand")} disabled={readOnly} />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Model</label>
                <input type="text" className="form-control" value={form.model || ""} onChange={setField("model")} disabled={readOnly} />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Make Year</label>
                <input type="number" className="form-control" value={form.makeYear ?? ""} onChange={setField("makeYear")} disabled={readOnly} />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Last Service Mileage</label>
                <input type="number" className="form-control" value={form.lastServiceMileage ?? ""} onChange={setField("lastServiceMileage")} disabled={readOnly} />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Next Service Due Mileage</label>
                <input type="number" className="form-control" value={form.nextServiceDueMileage ?? ""} onChange={setField("nextServiceDueMileage")} disabled={readOnly} />
              </div>
            </div>

            <hr />

            <h5 className="mb-1 d-inline-flex align-items-center gap-2"><Tag size={16} /> Allocation</h5>
            <p className="text-muted small mb-3">
              Every vehicle has one type. Staff and Sales vehicles are allocated to a named employee;
              Alarms, Operations and Technical vehicles are pool vehicles that may also name one.
            </p>
            <div className="row">
              <div className="col-md-4 mb-3">
                <label className="form-label">Vehicle Type</label>
                <VehicleTypeSelect id="vehicle-type" value={vehicleType} onChange={setVehicleType} disabled={readOnly} />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  Employee{" "}
                  <span className="text-muted fw-normal">
                    {vehicleType ? (employeeRequired ? "(required)" : "(optional)") : ""}
                  </span>
                </label>
                {resolvedEmployee ? (
                  <div>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="text"
                        className="form-control"
                        value={`${resolvedEmployee.empName} ${resolvedEmployee.empLastName || ""}`.trim()}
                        readOnly
                        disabled
                      />
                      {!readOnly && (
                        <>
                          <button
                            type="button"
                            className="btn btn-outline-secondary flex-shrink-0"
                            onClick={() => setResolvedEmployee(null)}
                          >
                            Change
                          </button>
                          {!employeeRequired && (
                            <button
                              type="button"
                              className="btn btn-outline-danger flex-shrink-0"
                              title="Make this a pool vehicle with no named employee"
                              onClick={() => setResolvedEmployee(null)}
                            >
                              Clear
                            </button>
                          )}
                        </>
                      )}
                    </div>
                    {/* promotedToEmpCode (Batch60) - this code is still active
                        and pickable until the WinForms side deactivates it, so
                        label rather than block the allocation. */}
                    {resolvedEmployee.promotedToEmpCode && (
                      <div className="text-muted fst-italic small mt-1">
                        Promoted → {resolvedEmployee.promotedToEmpCode}, pending
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="d-flex gap-2">
                    <input
                      type="text"
                      className="form-control"
                      placeholder={employeeRequired ? "Employee code (required)" : "Employee code (leave blank for a pool vehicle)"}
                      value={empCodeInput}
                      onChange={(e) => setEmpCodeInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleFindEmployee();
                        }
                      }}
                      disabled={readOnly || isSearchingEmpCode}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-primary flex-shrink-0"
                      onClick={handleFindEmployee}
                      disabled={readOnly || isSearchingEmpCode || !empCodeInput.trim()}
                    >
                      {isSearchingEmpCode ? "Searching..." : "Find"}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {!readOnly && (
              <div className="d-flex gap-2">
                <button type="button" className="btn btn-primary" onClick={handleSave} disabled={isCreating || isUpdating}>
                  Save
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => navigate(paths.vehicles)}>
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleForm;
